// Phase 2.3 (hh-1ns) — MoveItemSheet.
//
// Bottom-sheet for relocating an item. Ported from
// designproject/files/screens-card.jsx (`MoveItemSheet`). The design picked a
// room from a static `window.PROPERTIES`; here the destination tree is loaded
// from GET /api/v1/properties/tree and the confirm posts to
// POST /api/v1/items/{id}/move.
//
// case-14: when the item being moved is itself a container (some other item
// has `containerId === itemId`), a notice is shown before confirming so the
// user understands the whole content moves with it.

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T, Icon, Btn } from '../../tokens';
import { items, humanizeError } from '../../api';
import { usePropertyTree } from '../forms/usePropertyTree';

export interface MoveItemSheetProps {
  readonly itemId: string;
  /** True when the moved item is itself a container (case-14 notice). */
  readonly isContainer?: boolean;
  readonly onClose: () => void;
  /** Called with the new location label after a successful move. */
  readonly onMoved: (location: string) => void;
}

// Цель переноса — комната ИЛИ собственность напрямую (вещь без комнаты,
// привязана к property). Backend /items/{id}/move принимает оба варианта.
type MoveTarget =
  | { readonly kind: 'room'; readonly propertyId: string; readonly roomId: string }
  | { readonly kind: 'property'; readonly propertyId: string };

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function MoveItemSheet({
  itemId,
  isContainer = false,
  onClose,
  onMoved,
}: MoveItemSheetProps): JSX.Element {
  const tree = usePropertyTree();
  const [target, setTarget] = useState<MoveTarget | null>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = (): void => {
    if (target === null || pending) return;
    setPending(true);
    setError(null);
    const req =
      target.kind === 'room'
        ? { toRoomId: target.roomId, toPropertyId: target.propertyId }
        : { toPropertyId: target.propertyId };
    items
      .moveItem(itemId, req)
      .then((res) => {
        onMoved(res.location);
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Куда переносим?"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(31,27,22,0.4)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.bg,
          borderRadius: '22px 22px 0 0',
          padding: '16px 16px 24px',
          maxHeight: '78%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            background: T.borderStrong,
            borderRadius: 2,
            margin: '0 auto 14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
            Куда переносим?
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.ink3,
              padding: 0,
            }}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div style={{ fontSize: 13, color: T.ink3, marginBottom: 12 }}>
          Или скажи голосом: «переложи это в гараж»
        </div>

        {isContainer && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              marginBottom: 12,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              borderRadius: 12,
            }}
          >
            <Icon name="box" size={18} color={T.brandDeep} />
            <div style={{ fontSize: 13, fontWeight: 600, color: T.brandDeep }}>
              Контейнер — всё содержимое переедет вместе
            </div>
          </div>
        )}

        <div style={{ overflow: 'auto', flex: 1 }}>
          {tree.status === 'loading' && (
            <div style={{ fontSize: 13, color: T.ink3, padding: '12px 4px' }}>
              Загружаю места…
            </div>
          )}
          {tree.status === 'error' && (
            <div style={{ fontSize: 13, color: T.danger, padding: '12px 4px' }}>
              {tree.error ?? 'Не удалось загрузить места.'}
            </div>
          )}
          {tree.status === 'ready' && tree.properties.length === 0 && (
            <div style={{ fontSize: 13, color: T.ink3, padding: '12px 4px' }}>
              Нет объектов — сначала добавь хотя бы один.
            </div>
          )}
          {tree.status === 'ready' &&
            tree.properties.map((p) => {
              const propSelected = target !== null && target.kind === 'property' && target.propertyId === p.id;
              return (
                <div key={p.id}>
                  {/* Сама собственность — выбираемая цель: вещь без комнаты. */}
                  <TargetRow
                    icon="home"
                    label={p.name}
                    sublabel="перенести в объект, без помещения"
                    selected={propSelected}
                    onClick={() => setTarget({ kind: 'property', propertyId: p.id })}
                  />
                  {p.rooms.map((r) => {
                    const selected =
                      target !== null && target.kind === 'room' && target.roomId === r.id;
                    return (
                      <TargetRow
                        key={r.id}
                        icon="folder"
                        label={r.name}
                        indented
                        selected={selected}
                        onClick={() => setTarget({ kind: 'room', propertyId: p.id, roomId: r.id })}
                      />
                    );
                  })}
                </div>
              );
            })}
        </div>

        {error !== null && (
          <div
            style={{
              fontSize: 12.5,
              color: T.danger,
              fontWeight: 600,
              padding: '10px 4px 0',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1 }}>
            Отмена
          </Btn>
          <Btn
            kind="primary"
            icon="move"
            onClick={confirm}
            disabled={target === null || pending}
            style={{ flex: 1.5 }}
          >
            {pending ? 'Переношу…' : 'Перенести'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

interface TargetRowProps {
  readonly icon: 'home' | 'folder';
  readonly label: string;
  readonly sublabel?: string;
  readonly indented?: boolean;
  readonly selected: boolean;
  readonly onClick: () => void;
}

function TargetRow({
  icon,
  label,
  sublabel,
  indented = false,
  selected,
  onClick,
}: TargetRowProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: indented ? 'calc(100% - 16px)' : '100%',
        marginLeft: indented ? 16 : 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        background: selected ? T.brandSoft : T.surface,
        border: `1px solid ${selected ? T.brandSoft2 : T.border}`,
        borderRadius: 12,
        marginBottom: 6,
        marginTop: indented ? 0 : 8,
        cursor: 'pointer',
        fontFamily: T.font,
      }}
    >
      <Icon name={icon} size={16} color={selected ? T.brandDeep : T.ink2} />
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{label}</div>
        {sublabel !== undefined && (
          <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 1 }}>{sublabel}</div>
        )}
      </div>
      {selected && <Icon name="check" size={18} color={T.brandDeep} />}
    </button>
  );
}
